import { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionContext } from '../contexts/SessionContext';
import { apiFetch } from '../lib/api';
import { useI18n } from '../i18n';
import type { PersonaEmotion } from '@roll1/shared';
import { PERSONA_EMOTIONS } from '@roll1/shared';
import type { AiChatBase, AiChatMessage, AiConversationSummary } from './useAiChat';

/** Strip [EMOTION]: ... line from streaming content (client-side complement) */
const EMOTION_LINE_REGEX = /\n?\[EMOTION\]:\s*\w+\s*$/;

function stripEmotionLine(content: string): string {
  return content.replace(EMOTION_LINE_REGEX, '').trimEnd();
}

export type { AiChatBase as UsePlayerAiReturn };

// --- Constants ---

// Inter-chunk silence cap (resets on every streamed token). 60s was too tight:
// a tool-heavy import (create/fill a full sheet) runs a multi-iteration tool
// loop where a single provider.chat turn on a 16K-token payload can take 40-70s
// of silence before the next chunk, tripping the abort mid-import (prod logs:
// "Request was aborted" at ~74-127s). Match useAiChat's 300s GM cap.
const STREAMING_TIMEOUT_MS = 300_000;
// Keep in sync with useAiChat — generous cap for long pastes (character sheets).
const MAX_MESSAGE_LENGTH = 50_000;

// --- Hook ---

export function usePlayerAi(): AiChatBase {
  const { t } = useI18n();
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isAborted, setIsAborted] = useState(false);
  const [input, setInput] = useState('');

  const { mp } = useSessionContext();
  const sessionId = mp.session?.id;

  const conversationIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStreamingRef = useRef(false);
  const pendingDeltaRef = useRef('');
  const rafIdRef = useRef<number>(0);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  function clearStreamingTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  function resetStreamingTimeout() {
    clearStreamingTimeout();
    timeoutRef.current = setTimeout(() => {
      setIsStreaming(false);
      if (conversationIdRef.current) {
        mp.send({ type: 'ai:abort', conversationId: conversationIdRef.current });
      }
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error' as const,
        content: t('aiError.timeout'),
        timestamp: new Date().toISOString(),
      }]);
    }, STREAMING_TIMEOUT_MS);
  }

  useEffect(() => {
    return () => {
      clearStreamingTimeout();
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  function flushTextDelta(delta: string) {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant') {
        return [
          ...prev.slice(0, -1),
          { ...last, content: last.content + delta },
        ];
      }
      return [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant' as const,
        content: delta,
        timestamp: new Date().toISOString(),
      }];
    });
  }

  function scheduleFlush() {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;
      const delta = pendingDeltaRef.current;
      if (!delta) return;
      pendingDeltaRef.current = '';
      flushTextDelta(delta);
    });
  }

  // WS subscription for AI events
  useEffect(() => {
    const unsub = mp.onMessage((msg) => {
      if (!msg.type.startsWith('ai:')) return;

      if ('conversationId' in msg && conversationIdRef.current &&
          (msg as { conversationId: string }).conversationId !== conversationIdRef.current) return;

      resetStreamingTimeout();

      switch (msg.type) {
        case 'ai:text-delta':
          pendingDeltaRef.current += (msg as { delta: string }).delta;
          scheduleFlush();
          break;
        case 'ai:tool-start':
          setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role: 'tool' as const,
            content: '',
            toolData: {
              toolName: (msg as { toolName: string }).toolName,
              args: (msg as { args: Record<string, unknown> }).args,
              status: 'running' as const,
            },
            timestamp: (msg as { timestamp: string }).timestamp,
          }]);
          break;
        case 'ai:tool-result': {
          const toolName = (msg as { toolName: string }).toolName;
          const result = (msg as { result: string }).result;
          const success = (msg as { success: boolean }).success;
          setMessages(prev => {
            let idx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].role === 'tool' && prev[i].toolData?.toolName === toolName && prev[i].toolData?.status === 'running') {
                idx = i;
                break;
              }
            }
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              toolData: { ...updated[idx].toolData!, status: success ? 'done' : 'error', result, success },
            };
            return updated;
          });
          break;
        }
        case 'ai:done': {
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          setIsStreaming(false);
          clearStreamingTimeout();

          // Extract emotion from ai:done and apply to last assistant message
          const rawEmotion = (msg as { emotion?: string }).emotion;
          const validEmotion = rawEmotion && PERSONA_EMOTIONS.includes(rawEmotion as PersonaEmotion)
            ? rawEmotion as PersonaEmotion
            : undefined;

          setMessages(prev => {
            let lastIdx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].role === 'assistant') { lastIdx = i; break; }
            }
            if (lastIdx === -1) return prev;
            const updated = [...prev];
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: stripEmotionLine(updated[lastIdx].content),
              emotion: validEmotion,
            };
            return updated;
          });

          break;
        }
        case 'ai:error':
          setIsStreaming(false);
          clearStreamingTimeout();
          setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role: 'error' as const,
            content: (msg as { message: string }).message,
            timestamp: (msg as { timestamp: string }).timestamp,
          }]);
          break;
        case 'ai:aborted':
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          setIsStreaming(false);
          setIsAborted(true);
          clearStreamingTimeout();
          setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role: 'error' as const,
            content: t('ai.stoppedByGM'),
            timestamp: new Date().toISOString(),
          }]);
          break;
      }
    });
    return unsub;
  }, [mp]);

  const sendMessage = useCallback(async (text: string, _previewMode?: boolean) => {
    if (!sessionId || isStreamingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_MESSAGE_LENGTH) return;

    const userMsg: AiChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);
    setError(null);
    setIsAborted(false);

    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/player-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          conversationId: conversationIdRef.current ?? undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        let errorMsg: string = t('aiError.sendError');
        if (res.status === 429) errorMsg = data.code === 'TOKEN_BUDGET_EXCEEDED'
          ? t('aiError.tokenBudget') : t('aiError.rateLimit');
        else if (res.status === 503) errorMsg = t('aiError.serviceUnavailable');
        else if (res.status === 403) errorMsg = data.code === 'PLAYER_AI_DISABLED'
          ? t('aiError.playerAiDisabled') : (data.error ?? t('aiError.sendError'));
        else if (data.error) errorMsg = data.error;

        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: errorMsg,
          timestamp: new Date().toISOString(),
        }]);
        setIsStreaming(false);
        return;
      }

      const { conversationId: newConvId } = await res.json();
      setConversationId(newConvId);
      resetStreamingTimeout();
    } catch {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error',
        content: t('aiError.networkError'),
        timestamp: new Date().toISOString(),
      }]);
      setIsStreaming(false);
    }
  }, [sessionId]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setIsStreaming(false);
    setError(null);
    setIsAborted(false);
    setInput('');
    clearStreamingTimeout();
  }, []);

  const abortGeneration = useCallback(() => {
    if (!conversationIdRef.current || !isStreamingRef.current) return;
    mp.send({ type: 'ai:abort', conversationId: conversationIdRef.current });
    // Optimistic: immediately stop streaming UI so the button feels responsive.
    if (pendingDeltaRef.current) {
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
      flushTextDelta(pendingDeltaRef.current);
      pendingDeltaRef.current = '';
    }
    setIsStreaming(false);
    setIsAborted(true);
    clearStreamingTimeout();
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'error' as const,
      content: t('ai.stoppedByGM'),
      timestamp: new Date().toISOString(),
    }]);
  }, [mp, t]);

  // --- Conversation history ---

  const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const loadConversations = useCallback(async () => {
    if (!sessionId) return;
    setIsLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations);
      }
    } catch { /* ignore */ }
    finally { setIsLoadingHistory(false); }
  }, [sessionId]);

  const loadConversation = useCallback(async (convId: string) => {
    if (!sessionId) return;
    setIsLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations/${convId}`);
      if (res.ok) {
        const data = await res.json();
        setConversationId(convId);
        const clientMessages: AiChatMessage[] = data.messages
          .filter((m: { content: unknown }) => typeof m.content === 'string')
          .map((m: { role: string; content: string; timestamp: string }) => ({
            id: crypto.randomUUID(),
            role: m.role === 'assistant' ? 'assistant' as const : 'user' as const,
            content: m.content,
            timestamp: m.timestamp,
          }));
        setMessages(clientMessages);
      }
    } catch { /* ignore */ }
    finally { setIsLoadingHistory(false); }
  }, [sessionId]);

  // Derive lastEmotion from messages
  let lastEmotion: PersonaEmotion | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant' && messages[i].emotion) {
      lastEmotion = messages[i].emotion;
      break;
    }
  }

  return {
    messages, isStreaming, error, conversationId, sendMessage, resetChat,
    input, setInput, abortGeneration, isAborted,
    conversations, loadConversations, loadConversation, isLoadingHistory,
    lastEmotion,
  };
}
